import { Dispatch } from 'redux';
import PageController, {
  CreatePageResponse,
} from '../controllers/PageController';
import commonHandler from '../errorHandlers/common-handler';
import { common as pagesHandler } from '../errorHandlers/pages-handler';
import { ChatStarterKindSnakeType } from '../model/ChatStarterModel';
import PageModel, { PageKind } from '../model/PageModel';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import attributeCRUDActions from './AttributeCRUDActions';
import CrudActionContainer from './CrudActionContainer';
import variablesCRUDActions from './VariablesCRUDActions';
import viewActions from './viewActions';
import widgetCRUDActions from './WidgetCRUDActions';

export type CreatePageParams = {
  botId: number;
  name: string;
  notes?: string;
  pageGroupId?: number;
  parentId?: number;
  parentType?: 'campaign';
  sortOrder?: number;
  kind?: PageKind;
  platforms?: PageModel['platforms'];
  chatStarterKind?:
    | Exclude<ChatStarterKindSnakeType, 'all'>
    | 'chat_blaster'
    | 'drip_campaign';
};

type ClonePageParams = {
  chatbotId: number;
  pageId: number;
  targetPageId?: number;
};

class PageCRUDActions extends CrudActionContainer {
  retriveDropdownPagesAction: string;
  resetAction: string;
  cloneAction: string;
  retrievedByGroupId: string;

  constructor() {
    super('PAGE');
    this.retriveDropdownPagesAction = super.makeActionKey(
      'RETRIVE_DROPDOWN_PAGES',
    );
    this.resetAction = super.makeActionKey('RESET');
    this.cloneAction = super.makeActionKey('CLONE');
    this.retrievedByGroupId = super.makeActionKey('RETRIEVED_BY_GROUP_ID');
  }

  create(entity: CreatePageParams, showHidden?: boolean) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<CreatePageResponse> => {
      dispatch(this.busy(this.createAction));

      return PageController.postPage(entity)
        .then(response => {
          dispatch(this.retriveDropdownPages(entity.botId, showHidden));
          return response;
        })
        .catch(commonHandler(dispatch));
    };
  }

  retrieveAll(chatbotId: number, showHidden?: boolean) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<PageModel[] | undefined> => {
      dispatch(this.busy(this.retrieveAllAction));

      return PageController.getAll(chatbotId, undefined, showHidden)
        .then(response => {
          dispatch(this.retrievedAll(response));

          return response;
        })
        .catch(commonHandler(dispatch));
    };
  }

  retrieveById(pageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const page = await PageController.retrieveById(pageId);
        dispatch(this.retrieved(page));
        return page;
      } catch (e) {
        if (e instanceof Response) {
          if (e.status === 404) {
            return;
          }
        }
        return commonHandler(dispatch)(e);
      }
    };
  }

  batchRetrieveByIds(pageIds: number[]) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const pages = await PageController.batchRetrieveByIds(pageIds);
        if (pages.length) {
          pages.map(p => dispatch(this.retrieved(p)));
        }
        return pages;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  retrievePagesByGroupId(
    ...params: Parameters<typeof PageController.retrievePagesByGroupId>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const pages = await PageController.retrievePagesByGroupId(...params);
        if (pages.length) {
          dispatch({
            type: this.retrievedByGroupId,
            payload: pages,
          });
        }
        return pages;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  update(entity) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));

      return PageController.putPage(entity)
        .then(response => {
          dispatch(this.retriveDropdownPages(entity.botId));
          dispatch(this.updateLocal(entity));

          return response;
        })
        .catch(commonHandler(dispatch));
    };
  }

  batchUpdate(entities: Partial<PageModel>[]) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.localBatchUpdate(entities));
        const response = await PageController.patch(entities);
        dispatch(this.retrievedBatch(response));
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  delete(chatbotId: number, pageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(viewActions.deactivatePageEditor());
      dispatch(this.busy(this.deleteAction));
      try {
        const response = await PageController.deletePage(pageId);
        dispatch(this.retriveDropdownPages(chatbotId));
        return response;
      } catch (e) {
        return pagesHandler(dispatch)(e, chatbotId);
      }
    };
  }

  clone({ chatbotId, pageId, targetPageId }: ClonePageParams) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.cloneAction));
      try {
        const response = await PageController.clone<PageModel>(
          pageId,
          targetPageId,
        );
        dispatch(this.retriveDropdownPages(chatbotId));
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  setDropdownPages(pages) {
    return {
      type: this.retriveDropdownPagesAction,
      payload: pages,
    };
  }

  retriveDropdownPages(chatbotId: number, showHidden?: boolean) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const response = await PageController.getDropdownPages(
          chatbotId,
          showHidden,
        );
        dispatch(this.setDropdownPages(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  reset() {
    return {
      type: this.resetAction,
    };
  }

  importFromSearchToken(targetDialogueId: number, searchToken: string) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const chatbotId = activeChatbotIdSelector(state);
      const facebookPageId = activeFacebookPageIdSelector(state);
      try {
        dispatch(this.busy());
        await PageController.importFromSearchToken(
          targetDialogueId,
          searchToken,
        );
        await Promise.all([
          dispatch(pageCRUDActions.retrieveAll(chatbotId)),
          dispatch(widgetCRUDActions.retrieveAll(targetDialogueId)),
          dispatch(variablesCRUDActions.retrieveAll(facebookPageId)),
          dispatch(attributeCRUDActions.retrieveAll(facebookPageId)),
        ]);
        return true;
      } catch (e) {
        if (e instanceof Response && e.status === 404) {
          return false;
        }
        await commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }
}

const pageCRUDActions = new PageCRUDActions();

export default pageCRUDActions;

export const usePageCRUDActions = getActionHookBuilder(pageCRUDActions, [
  'batchUpdate',
  'batchRetrieveByIds',
  'clone',
  'create',
  'delete',
  'retrieveAll',
  'retriveDropdownPages',
  'retrieveById',
  'retrievePagesByGroupId',
  'update',
  'importFromSearchToken',
]);
